import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Lock, ArrowLeft, ThumbsUp, Printer, Type, Minus, Plus, UserPlus, UserCheck, Sun, Moon, Gift, Music, ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Narrator } from "@/components/Narrator";
import { CharacterChat } from "@/components/CharacterChat";
import { safeStorage } from "@/lib/safeStorage";

export default function Reader() {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [author, setAuthor] = useState(null);
  const { user } = useAuth();
  const printRef = useRef(null);

  // Reader comfort state
  const [fontSize, setFontSize] = useState(() => parseInt(safeStorage.getItem("mythos_fontSize") || "18"));
  const [lineHeight, setLineHeight] = useState(() => parseFloat(safeStorage.getItem("mythos_lineHeight") || "1.9"));
  const [theme, setTheme] = useState(() => safeStorage.getItem("mythos_readerTheme") || "dark");

  useEffect(() => { safeStorage.setItem("mythos_fontSize", String(fontSize)); }, [fontSize]);
  useEffect(() => { safeStorage.setItem("mythos_lineHeight", String(lineHeight)); }, [lineHeight]);
  useEffect(() => { safeStorage.setItem("mythos_readerTheme", theme); }, [theme]);

  useEffect(() => {
    setLoading(true);
    api.get(`/stories/${id}`).then((r) => {
      setStory(r.data);
      if (r.data && r.data.author_id && r.data.author_id !== "system") {
        api.get(`/authors/${r.data.author_id}`).then((a) => setAuthor(a.data)).catch(() => {});
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const vote = async () => {
    try {
      await api.post(`/stories/${id}/vote`);
      toast.success("Your vote is recorded");
      setStory({ ...story, votes: (story.votes || 0) + 1 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    }
  };

  // ---- Author-only: attach uploaded music / art to a specific chapter ----
  const [myTracks, setMyTracks] = useState([]);
  const [myArts, setMyArts] = useState([]);
  useEffect(() => {
    if (user && story && user.user_id === story.author_id) {
      api.get("/me/assets", { params: { kind: "user_music" } }).then(r => setMyTracks(r.data)).catch(() => {});
      api.get("/me/assets", { params: { kind: "user_art" } }).then(r => setMyArts(r.data)).catch(() => {});
    }
  }, [user, story]);
  const attachTrack = async (chapterIdx, fileId) => {
    try {
      const { data } = await api.post(`/stories/${id}/chapter-track`, {
        chapter_index: chapterIdx,
        track_file_id: fileId || null,
      });
      const next = { ...story, chapters: story.chapters.map((c, i) => i === chapterIdx ? { ...c, track_url: data.track_url, track_file_id: fileId || undefined } : c) };
      setStory(next);
      toast.success(fileId ? "Track attached" : "Track cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const attachArt = async (chapterIdx, fileId) => {
    try {
      const { data } = await api.post(`/stories/${id}/chapter-art`, {
        chapter_index: chapterIdx,
        art_file_id: fileId || null,
      });
      const next = { ...story, chapters: story.chapters.map((c, i) => i === chapterIdx ? { ...c, art_url: data.art_url, art_file_id: fileId || undefined } : c) };
      setStory(next);
      toast.success(fileId ? "Painting pinned" : "Painting cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow authors"); return; }
    try {
      if (author?.is_following) {
        await api.delete(`/follows/${story.author_id}`);
        setAuthor({ ...author, is_following: false, followers: Math.max(0, author.followers - 1) });
        toast("Unfollowed", { description: story.author_name });
      } else {
        await api.post(`/follows/${story.author_id}`);
        setAuthor({ ...author, is_following: true, followers: (author?.followers || 0) + 1 });
        toast.success(`Following ${story.author_name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const doPrint = () => window.print();

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Turning the page…</div>;
  if (!story) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Story not found.</div>;

  const themeStyles = {
    dark: { bg: "transparent", color: "#E2E8F0" },
    sepia: { bg: "#F5EBD8", color: "#3B2F1E" },
    light: { bg: "#FFFFFF", color: "#1B1B1B" },
  }[theme];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16" style={{background: themeStyles.bg}}>
      <div className="flex items-center justify-between mb-8 no-print">
        <Link to="/stories" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300" data-testid="reader-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to library
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Narrator story={story} />
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="reader-comfort" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:border-amber-500/40">
                <Type className="h-3.5 w-3.5" /> Reader
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 bg-[#0F1117] border-white/10 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Font size</span>
                  <span className="font-mono text-xs text-amber-300">{fontSize}px</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="font-dec" size="icon" variant="outline" className="h-8 w-8" onClick={() => setFontSize((s) => Math.max(14, s - 1))}><Minus className="h-3 w-3" /></Button>
                  <Slider min={14} max={28} step={1} value={[fontSize]} onValueChange={(v) => setFontSize(v[0])} />
                  <Button data-testid="font-inc" size="icon" variant="outline" className="h-8 w-8" onClick={() => setFontSize((s) => Math.min(28, s + 1))}><Plus className="h-3 w-3" /></Button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Line height</span>
                  <span className="font-mono text-xs text-amber-300">{lineHeight.toFixed(2)}</span>
                </div>
                <Slider min={1.4} max={2.4} step={0.05} value={[lineHeight]} onValueChange={(v) => setLineHeight(v[0])} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">Theme</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {k: "dark", label: "Dark", icon: Moon},
                    {k: "sepia", label: "Sepia", icon: Sun},
                    {k: "light", label: "Light", icon: Sun},
                  ].map((t) => (
                    <button key={t.k} data-testid={`theme-${t.k}`} onClick={() => setTheme(t.k)} className={`rounded-lg border px-3 py-2 text-xs flex items-center gap-1 justify-center ${theme === t.k ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-white/10 text-slate-300 hover:border-white/20"}`}>
                      <t.icon className="h-3 w-3" /> {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button data-testid="print-btn" variant="outline" size="sm" onClick={doPrint} className="rounded-full border-white/10 text-slate-300 h-8">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div ref={printRef} className="print-area">
        <div className="grid md:grid-cols-[220px_1fr] gap-8 mb-10">
          <div className="rounded-2xl overflow-hidden border border-white/10">
            {story.cover_url ? <img src={story.cover_url} alt="" className="w-full aspect-[3/4] object-cover" /> :
              <div className="aspect-[3/4] bg-gradient-to-br from-amber-900/40 to-slate-900" />}
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 mb-2">{story.genre}</div>
            {["pending", "rejected"].includes(story.approval_status) && (
              <div data-testid="reader-approval-banner" className={`mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-widest ${story.approval_status === "pending" ? "border-amber-400/40 text-amber-300 bg-amber-500/10" : "border-rose-400/40 text-rose-300 bg-rose-500/10"}`}>
                {story.approval_status === "pending" ? "Awaiting editor approval · only you can see this" : `Not approved${story.admin_note ? ` · ${story.admin_note}` : ""}`}
              </div>
            )}
            <h1 data-testid="reader-title" className="font-serif text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{color: themeStyles.color}}>{story.title}</h1>
            <p className="mt-3 opacity-80" style={{color: themeStyles.color}}>{story.tagline}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs">
              <span className="text-slate-500">by <span className="text-slate-300">{story.author_name}</span></span>
              {story.author_id !== "system" && author && user && user.user_id !== story.author_id && (
                <button
                  data-testid="follow-author"
                  onClick={toggleFollow}
                  className={`no-print inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium transition ${author.is_following ? "bg-amber-500/15 border border-amber-500/40 text-amber-300" : "bg-white/5 border border-white/10 text-slate-200 hover:border-amber-400/50"}`}
                >
                  {author.is_following ? <UserCheck className="h-3 w-3" /> : <UserPlus className="h-3 w-3" />}
                  {author.is_following ? "Following" : "Follow"}
                </button>
              )}
              {author && (
                <span className="text-slate-500 no-print">{author.followers} follower{author.followers === 1 ? "" : "s"}</span>
              )}
              <button data-testid="vote-btn" onClick={vote} disabled={!user} className="no-print inline-flex items-center gap-1 text-slate-400 hover:text-rose-400 disabled:opacity-40">
                <ThumbsUp className="h-3.5 w-3.5" /> {story.votes || 0}
              </button>
              {user && story.author_id !== user.user_id && (
                <button
                  data-testid="tip-btn"
                  onClick={async () => {
                    const amt = Number(prompt(`Tip ${story.author_name} how many gems?`, "25"));
                    if (!amt || amt < 1) return;
                    try {
                      const { data } = await (await import("@/lib/api")).api.post(`/stories/${story.story_id}/tip`, { amount: amt });
                      toast.success(`Tipped ${amt} gems · ${data.author_cut} went to ${story.author_name}`);
                      setStory({ ...story, total_tips: (story.total_tips || 0) + amt });
                      if (data.milestone_triggered) {
                        // Confetti burst 🎉
                        try {
                          const end = Date.now() + 2500;
                          (function frame() {
                            confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ["#fbbf24", "#f43f5e", "#fff"] });
                            confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ["#fbbf24", "#f43f5e", "#fff"] });
                            if (Date.now() < end) requestAnimationFrame(frame);
                          })();
                        } catch {}
                        toast.success("🎉 1000-gem milestone! A bonus chapter is being written for this story.", { duration: 7000 });
                        // Refetch story after a few seconds — bonus chapter arrives via background Claude call
                        setTimeout(() => api.get(`/stories/${story.story_id}`).then(r => setStory(r.data)), 15000);
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Tip failed");
                    }
                  }}
                  className="no-print inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 h-7 px-3 text-xs"
                >
                  💎 Tip · {story.total_tips || 0}
                </button>
              )}
              {story.milestone_hit && (
                <span data-testid="milestone-badge" className="no-print inline-flex items-center gap-1 rounded-full border border-rose-400/50 bg-rose-500/10 text-rose-200 h-7 px-3 text-xs font-mono uppercase tracking-widest">
                  <Gift className="h-3 w-3" /> Beloved · 1000+
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="reader-prose" style={{color: themeStyles.color, fontSize: `${fontSize}px`, lineHeight}}>
          {story.chapters.map((ch, i) => (
            <div key={i} data-testid={`chapter-${i}`} className="mb-10">
              <h2 className="font-serif mb-4 flex items-center gap-3 flex-wrap" style={{color: theme === "dark" ? "#FCD34D" : theme === "sepia" ? "#7C3D00" : "#111", fontSize: `${Math.round(fontSize * 1.5)}px`}}>
                <span>{ch.title}</span>
                {ch.is_bonus && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-rose-400/50 bg-rose-500/10 text-rose-200 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5">
                    <Gift className="h-3 w-3" /> Bonus · unlocked by tips
                  </span>
                )}
                {user && story.author_id === user.user_id && (
                  <>
                    <select
                      data-testid={`attach-track-${i}`}
                      value={ch.track_file_id || ""}
                      onChange={(e) => attachTrack(i, e.target.value || null)}
                      className="no-print ml-auto text-xs bg-[#07080A] border border-white/10 text-slate-300 rounded-full h-8 px-3 font-mono"
                      title="Attach your uploaded music to this chapter"
                    >
                      <option value="">No track</option>
                      {myTracks.map(t => (
                        <option key={t.file_id} value={t.file_id}>🎵 {t.title || t.file_id.slice(0, 10)}</option>
                      ))}
                    </select>
                    <select
                      data-testid={`attach-art-${i}`}
                      value={ch.art_file_id || ""}
                      onChange={(e) => attachArt(i, e.target.value || null)}
                      className="no-print text-xs bg-[#07080A] border border-white/10 text-slate-300 rounded-full h-8 px-3 font-mono"
                      title="Pin one of your paintings above this chapter"
                    >
                      <option value="">No painting</option>
                      {myArts.map(a => (
                        <option key={a.file_id} value={a.file_id}>🖼 {a.title || a.file_id.slice(0, 10)}</option>
                      ))}
                    </select>
                  </>
                )}
              </h2>
              {ch.art_url && (
                <figure data-testid={`chapter-art-${i}`} className="mb-5 rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                  <img src={fileUrl(ch.art_url)} alt={`${ch.title} — painting`} className="w-full max-h-[460px] object-cover" />
                </figure>
              )}
              {ch.track_url && (
                <div className="no-print mb-4 rounded-xl border border-white/10 bg-black/40 p-3 flex items-center gap-3">
                  <Music className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <audio data-testid={`chapter-track-${i}`} controls preload="none" src={fileUrl(ch.track_url)} className="w-full h-9" />
                </div>
              )}
              {ch.content.split(/\n+/).map((para, j) => <p key={j} style={{fontSize: `${fontSize}px`, lineHeight}}>{para}</p>)}
            </div>
          ))}
        </div>
      </div>

      {story.locked && (
        <div data-testid="paywall" className="relative -mt-24 pt-24 paywall-mask no-print">
          <div className="glass-card rounded-3xl p-8 md:p-12 text-center border-amber-500/30">
            <Lock className="h-8 w-8 text-amber-400 mx-auto mb-4" />
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-2">Unlock the rest of the story</h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">Subscribe to ProSeCraft Reader to read every chapter of every tale — plus new stories weekly.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button data-testid="paywall-subscribe" asChild className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full h-12 px-7">
                <Link to="/pricing">See subscription plans</Link>
              </Button>
              <Button data-testid="paywall-create" asChild variant="outline" className="rounded-full h-12 px-7 border-white/15">
                <Link to="/create">Or create your own — free</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
      <CharacterChat story={story} />
    </div>
  );
}
